import { useMemo, useState } from 'react'
import { Filter, Plus } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { cn } from 'lib/utils'

import { FilterChip } from './FilterChip'
import { GROUP_ORDER } from '../constants'
import type { ExplorerColumn, ExplorerFilter, ExplorerGrainKey } from '../types/explorer.types'

interface FilterBarProps {
	/** The whole catalog, not just the selected columns. */
	catalog: ExplorerColumn[]
	filters: ExplorerFilter[]
	grain: ExplorerGrainKey
	onChange: (filters: ExplorerFilter[]) => void
}

/**
 * The filter chip row and its Add filter menu (MYD-368).
 *
 * ## A filter may target a column that is not displayed
 *
 * The engine filters against the full catalog, and `useExplorerDefinition`
 * deliberately keeps a filter when its column is hidden, because dropping it
 * would silently change which rows come back. So this bar offers EVERY
 * filterable column, not just the selected ones, and it is the only place a
 * filter on a hidden column is visible at all.
 */
export function FilterBar({ catalog, filters, grain, onChange }: FilterBarProps) {
	const [menuOpen, setMenuOpen] = useState(false)
	const [search, setSearch] = useState('')
	// The chip for a filter the user has just added opens straight into its
	// editor: adding one and then having to click it again is a wasted step.
	const [justAdded, setJustAdded] = useState<string | null>(null)

	const byKey = useMemo(() => new Map(catalog.map(c => [c.key, c])), [catalog])

	const groups = useMemo(() => {
		const query = search.trim().toLowerCase()
		const used = new Set(filters.map(f => f.key))

		const available = catalog.filter(column => {
			if (!column.filterable || used.has(column.key)) return false
			if (!query) return true
			return column.label.toLowerCase().includes(query) || column.group.toLowerCase().includes(query)
		})

		const byGroup = new Map<string, ExplorerColumn[]>()
		for (const column of available) {
			byGroup.set(column.group, [...(byGroup.get(column.group) ?? []), column])
		}

		return [...byGroup.entries()].sort(([a], [b]) => {
			const ai = GROUP_ORDER.indexOf(a)
			const bi = GROUP_ORDER.indexOf(b)
			if (ai === -1 && bi === -1) return a.localeCompare(b)
			if (ai === -1) return 1
			if (bi === -1) return -1
			return ai - bi
		})
	}, [catalog, filters, search])

	const add = (column: ExplorerColumn) => {
		// Added with no value, so it does not narrow anything until the user
		// chooses one. The engine skips a filter carrying no usable bound, so an
		// empty chip is inert rather than an error.
		onChange([...filters, { key: column.key }])
		setJustAdded(column.key)
		setMenuOpen(false)
		setSearch('')
	}

	const update = (key: string, next: ExplorerFilter) =>
		onChange(filters.map(f => (f.key === key ? next : f)))

	const remove = (key: string) => onChange(filters.filter(f => f.key !== key))

	return (
		<div className="flex items-center gap-2 flex-wrap mb-4">
			<Filter className="w-3.5 h-3.5 text-gray-400 shrink-0" />

			{filters.map(filter => {
				const column = byKey.get(filter.key)
				// A saved report can outlive a column. Rather than drop the filter
				// silently, which would change the result set, show it as unresolved
				// so the user can see what to remove.
				if (!column) {
					return (
						<span
							key={filter.key}
							className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-400"
							title="This column is no longer available"
						>
							{filter.key}
							<button
								type="button"
								onClick={() => remove(filter.key)}
								aria-label={`Remove ${filter.key} filter`}
								className="hover:text-destructive"
							>
								×
							</button>
						</span>
					)
				}

				return (
					<FilterChip
						key={filter.key}
						column={column}
						filter={filter}
						grain={grain}
						autoOpen={justAdded === filter.key}
						onChange={next => update(filter.key, next)}
						onRemove={() => remove(filter.key)}
					/>
				)
			})}

			<Popover open={menuOpen} onOpenChange={setMenuOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900"
					>
						<Plus className="w-3 h-3" />
						Add filter
					</button>
				</PopoverTrigger>

				<PopoverContent align="start" className="w-64 p-2">
					<Input
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Search columns..."
						aria-label="Search filterable columns"
						className="mb-2"
					/>

					<div className="max-h-72 overflow-y-auto">
						{groups.length === 0 ? (
							<p className="text-xs text-gray-400 px-2 py-2">
								{search ? 'No columns match your search.' : 'Every filterable column is in use.'}
							</p>
						) : (
							groups.map(([group, columns]) => (
								<div key={group} className="mb-2 last:mb-0">
									<p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-2 mb-0.5">
										{group}
									</p>
									{columns.map(column => (
										<button
											key={column.key}
											type="button"
											onClick={() => add(column)}
											className={cn(
												'w-full px-2 py-1.5 rounded-md text-left text-sm text-gray-700 hover:bg-gray-50',
											)}
										>
											<span className="truncate">{column.label}</span>
										</button>
									))}
								</div>
							))
						)}
					</div>
				</PopoverContent>
			</Popover>

			{filters.length > 0 && (
				<Button variant="text" size="input" onClick={() => onChange([])}>
					Clear all
				</Button>
			)}
		</div>
	)
}

export default FilterBar
